import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { vendorService, authService } from '../services/api';
import { useToast } from '../hooks/useToast';
import {
  LayoutDashboard,
  ClipboardCheck,
  CreditCard,
  Ticket,
  Users,
  Tags,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  FileSpreadsheet,
  UserCheck,
  ShieldAlert,
  MessageSquare,
  BookOpen
} from 'lucide-react';

// Magazine imports
import MagazineOverview from './admin/magazine/MagazineOverview';
import MagazineSubscribers from './admin/magazine/MagazineSubscribers';
import MagazineOrders from './admin/magazine/MagazineOrders';
import MagazineManual from './admin/magazine/MagazineManual';
import MagazineManagement from './admin/magazine/MagazineManagement';

// Modular Tab imports
import OverviewTab from './admin/overview/OverviewTab';
import ListingApprovalsTab from './admin/listings/ListingApprovalsTab';
import ExcelImportTab from './admin/listings/ExcelImportTab';
import ClaimRequestsTab from './admin/listings/ClaimRequestsTab';
import UnclaimedUpdateTab from './admin/listings/UnclaimedUpdateTab';
import VendorListingManagerTab from './admin/listings/VendorListingManagerTab';
import MembershipAuditTab from './admin/memberships/MembershipAuditTab';
import IncompleteMembershipsTab from './admin/memberships/IncompleteMembershipsTab';
import ManualMembershipsTab from './admin/memberships/ManualMembershipsTab';
import SupportTicketsTab from './admin/support/SupportTicketsTab';
import ReportedReviewsTab from './admin/support/ReportedReviewsTab';
import ManageUsersTab from './admin/users/ManageUsersTab';
import InquiriesTab from './admin/users/InquiriesTab';
import CategoriesTagsTab from './admin/catalog/CategoriesTagsTab';

const AdminDashboard = () => {
  const { user, hasPermission, logout, roles, hasRole } = useAuth();
  const { toast } = useToast();

  const getAdminDashboardTitle = () => {
    if (!roles) return 'Admin Dashboard';
    if (roles.includes('ROLE_SUPER_ADMIN')) return 'Super Admin Dashboard';
    if (roles.includes('ROLE_ADMIN')) return 'Admin Dashboard';
    if (roles.includes('ROLE_SALES_ADMIN')) return 'Sales Admin Dashboard';
    if (roles.includes('ROLE_LISTING_ADMIN')) return 'Listing Admin Dashboard';
    if (roles.includes('ROLE_SUPPORT_ADMIN')) return 'Support Admin Dashboard';
    return 'Admin Dashboard';
  };

  // ─── TAB PERSISTENCE: URL hash-based (history.replaceState) ────────────────
  // All valid tab keys in the dashboard.
  const VALID_TABS = [
    'overview', 'listings', 'excel_import', 'claim_requests', 'unclaimed_update',
    'vendor_listing_manager',
    'memberships_audit', 'memberships_incomplete', 'memberships_manual',
    'support', 'reported_reviews', 'users', 'inquiries', 'categories',
    'magazine_overview', 'magazine_subscribers', 'magazine_orders',
    'magazine_manual', 'magazine_management',
  ];

  // Mirrors the exact same permission guards used to render each tab section.
  // Used to validate a hash-restored tab before applying it, so a demoted
  // admin never gets a blank content area on refresh.
  const canAccessTab = (tab) => {
    if (tab === 'overview') {
      return !roles.includes('ROLE_MAGAZINE_SALES') || roles.includes('ROLE_SUPER_ADMIN') || roles.includes('ROLE_ADMIN');
    }
    if (['listings', 'excel_import', 'claim_requests', 'unclaimed_update', 'vendor_listing_manager'].includes(tab)) {
      return hasPermission('APPROVE_LISTINGS');
    }
    if (['memberships_audit', 'memberships_incomplete', 'inquiries'].includes(tab)) {
      return hasPermission('MANAGE_MEMBERSHIPS');
    }
    if (tab === 'memberships_manual') {
      return hasPermission('MANAGE_MEMBERSHIPS') && hasRole && hasRole('ADMIN');
    }
    if (['support', 'reported_reviews'].includes(tab)) {
      return hasPermission('RESOLVE_ISSUES');
    }
    if (tab === 'users') {
      return hasPermission('MANAGE_USERS');
    }
    if (tab === 'categories') {
      return hasPermission('MANAGE_CATALOG');
    }
    if (['magazine_overview', 'magazine_subscribers', 'magazine_orders'].includes(tab)) {
      return hasPermission('MANAGE_MEMBERSHIPS') || roles.includes('ROLE_MAGAZINE_SALES');
    }
    if (['magazine_manual', 'magazine_management'].includes(tab)) {
      return roles.includes('ROLE_SUPER_ADMIN') || roles.includes('ROLE_ADMIN');
    }
    return false;
  };

  // Returns the correct default tab based on the user's role.
  const getRoleDefaultTab = () =>
    roles.includes('ROLE_MAGAZINE_SALES') && !roles.includes('ROLE_SUPER_ADMIN') && !roles.includes('ROLE_ADMIN')
      ? 'magazine_overview'
      : 'overview';

  // Reads the URL hash on first render, validates it against VALID_TABS and the
  // user's live permissions. Falls back to role-based default if the hash is
  // missing, invalid, or the user no longer has access to that tab.
  const getInitialTab = () => {
    const hash = window.location.hash.replace('#', '').toLowerCase().trim();
    if (VALID_TABS.includes(hash) && canAccessTab(hash)) {
      return hash;
    }
    return getRoleDefaultTab();
  };
  // ─────────────────────────────────────────────────────────────────────────────

  const [activeTab, setActiveTab] = useState(getInitialTab);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Listing Tab state (retained only for notification center redirect logic)
  const [listingSubTab, setListingSubTab] = useState('new');

  // Sidebar dropdown toggle states
  const [membershipsMenuOpen, setMembershipsMenuOpen] = useState(false);
  const [magazineMenuOpen, setMagazineMenuOpen] = useState(false);
  const [bulkImportMenuOpen, setBulkImportMenuOpen] = useState(false);

  // Global Lightbox image state
  const [lightboxImage, setLightboxImage] = useState(null);

  // Notifications State & Refs
  const [notifications, setNotifications] = useState([]);
  const [totalNotificationCount, setTotalNotificationCount] = useState(0);
  const [notificationMenuOpen, setNotificationMenuOpen] = useState(false);
  const notificationRef = useRef(null);

  // Unified fetch for counts and list. Automatically filters out local dismissed IDs.
  // Cleans up stale dismissed IDs from localStorage if they are no longer active on the server.
  const fetchNotifications = useCallback(async (updateList = false) => {
    try {
      const res = await vendorService.getAdminNotificationCounts(true);
      if (res.success && res.data) {
        const activeNotifications = res.data.notifications || [];
        const activeIds = activeNotifications.map(n => n.id);

        const dismissedIds = JSON.parse(localStorage.getItem('dismissed_notification_ids') || '[]');
        const cleanedDismissed = dismissedIds.filter(id => activeIds.includes(id));
        localStorage.setItem('dismissed_notification_ids', JSON.stringify(cleanedDismissed));

        const visibleNotifications = activeNotifications.filter(n => !cleanedDismissed.includes(n.id));

        setTotalNotificationCount(visibleNotifications.length);
        if (updateList) {
          setNotifications(visibleNotifications);
        }
      }
    } catch (err) {
      console.error("Error fetching notifications:", err);
    }
  }, []);

  const handleNotificationClick = (n) => {
    setNotificationMenuOpen(false);

    // Save dismissed state in localStorage
    const dismissedIds = JSON.parse(localStorage.getItem('dismissed_notification_ids') || '[]');
    if (!dismissedIds.includes(n.id)) {
      dismissedIds.push(n.id);
      localStorage.setItem('dismissed_notification_ids', JSON.stringify(dismissedIds));
    }

    // Instantly remove from local UI states
    setNotifications(prev => prev.filter(item => item.id !== n.id));
    setTotalNotificationCount(prev => Math.max(0, prev - 1));

    if (n.type === 'LISTING_NEW' || n.type === 'LISTING_UPDATED') {
      setActiveTab('listings');
      setListingSubTab(n.type === 'LISTING_NEW' ? 'new' : 'updated');
    } else if (n.type === 'TICKET_NEW') {
      setActiveTab('support');
    } else if (n.type === 'MEMBERSHIP_NEW') {
      setActiveTab('memberships_audit');
    } else if (n.type === 'MEMBERSHIP_INCOMPLETE') {
      setActiveTab('memberships_incomplete');
    } else if (n.type === 'USER_NEW') {
      setActiveTab('users');
    } else if (n.type === 'USER_NO_LISTING') {
      setActiveTab('listings');
      setListingSubTab('no_listing');
    } else if (n.type === 'CLAIM_NEW') {
      setActiveTab('claim_requests');
    } else if (n.type === 'MAGAZINE_SUBSCRIBER_NEW') {
      setActiveTab('magazine_subscribers');
    } else if (n.type === 'MAGAZINE_ORDER_NEW') {
      setActiveTab('magazine_orders');
    }
  };

  // Unified notifications fetch effect (polling, tab change, menu toggle)
  useEffect(() => {
    const updateList = notificationMenuOpen;
    fetchNotifications(updateList);
    const interval = setInterval(() => {
      fetchNotifications(updateList);
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications, notificationMenuOpen, activeTab, listingSubTab]);

  // Click outside for notifications menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setNotificationMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-expand sidebar menus
  useEffect(() => {
    if (activeTab === 'memberships_audit' || activeTab === 'memberships_incomplete' || activeTab === 'memberships_manual') {
      setMembershipsMenuOpen(true);
    }
    if (['magazine_overview', 'magazine_subscribers', 'magazine_orders', 'magazine_manual', 'magazine_management'].includes(activeTab)) {
      setMagazineMenuOpen(true);
    }
    if (['unclaimed_update', 'claim_requests', 'excel_import'].includes(activeTab)) {
      setBulkImportMenuOpen(true);
    }
  }, [activeTab]);

  // Persist the active tab to the URL hash using replaceState.
  // replaceState updates the URL silently — it creates NO browser history
  // entry, so the Back/Forward buttons are completely unaffected.
  // The hash survives a page refresh, so the admin reopens the same tab.
  useEffect(() => {
    window.history.replaceState(null, '', `#${activeTab}`);
  }, [activeTab]);

  return (
    <div className="flex min-h-screen bg-slate-100 font-sans text-slate-800 antialiased overflow-x-hidden">

      {/* MOBILE HEADER BAR */}
      <header className="lg:hidden flex items-center justify-between bg-slate-900 text-slate-100 px-6 py-4 fixed top-0 left-0 right-0 z-40 border-b border-slate-800 select-none">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800 transition-colors cursor-pointer"
          >
            <Menu className="w-6 h-6" />
          </button>
          <img src="/Top_Interior_India_2.png" alt="Top Interiors Logo" className="h-8 object-contain" />
        </div>
        <div className="text-xs font-bold text-slate-300 tracking-wider">Admin Center</div>
      </header>

      {/* MOBILE DRAWER BACKDROP */}
      {mobileMenuOpen && (
        <div
          onClick={() => setMobileMenuOpen(false)}
          className="lg:hidden fixed inset-0 bg-slate-950/70 backdrop-blur-sm z-50 animate-in fade-in duration-200"
        />
      )}

      {/* MOBILE DRAWER SIDEBAR */}
      <aside className={`lg:hidden fixed inset-y-0 left-0 w-80 bg-slate-900 text-slate-100 py-6 px-6 flex flex-col z-50 border-r border-slate-800 transition-transform duration-300 ease-out ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}>
        <div className="flex items-center justify-between pb-6 border-b border-slate-800 mb-6 select-none">
          <img src="/Top_Interior_India_2.png" alt="Top Interiors Logo" className="h-9 object-contain" />
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 space-y-1.5 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-800 scrollbar-track-transparent select-none">
          {/* 1. Overview */}
          {(!roles.includes('ROLE_MAGAZINE_SALES') || roles.includes('ROLE_SUPER_ADMIN') || roles.includes('ROLE_ADMIN')) && (
            <button
              onClick={() => { setActiveTab('overview'); setMobileMenuOpen(false); }}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all w-full text-left cursor-pointer group ${activeTab === 'overview'
                ? 'bg-[#2ab391] text-white shadow-lg shadow-[#2ab391]/10'
                : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-202'
                }`}
            >
              <LayoutDashboard className="w-4 h-4 text-slate-400 group-hover:text-slate-202" />
              Overview
            </button>
          )}

          {/* 2. Listing */}
          {hasPermission('APPROVE_LISTINGS') && (
            <>
              <button
                onClick={() => { setActiveTab('listings'); setMobileMenuOpen(false); }}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all w-full text-left cursor-pointer group ${activeTab === 'listings'
                  ? 'bg-[#2ab391] text-white shadow-lg shadow-[#2ab391]/10'
                  : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-202'
                  }`}
              >
                <ClipboardCheck className="w-4 h-4 text-slate-400 group-hover:text-slate-202" />
                Listing
              </button>
              <button
                onClick={() => { setActiveTab('vendor_listing_manager'); setMobileMenuOpen(false); }}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all w-full text-left cursor-pointer group mt-1 ${activeTab === 'vendor_listing_manager'
                  ? 'bg-[#2ab391] text-white shadow-lg shadow-[#2ab391]/10'
                  : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-202'
                  }`}
              >
                <UserCheck className="w-4 h-4 text-slate-400 group-hover:text-slate-202" />
                Vendor Listing Manager
              </button>
            </>
          )}

          {/* 3. Memberships & Sales */}
          {hasPermission('MANAGE_MEMBERSHIPS') && (
            <div className="flex flex-col w-full">
              <button
                onClick={() => setMembershipsMenuOpen(!membershipsMenuOpen)}
                className={`flex items-center justify-between px-4 py-3 rounded-xl text-sm font-semibold transition-all w-full text-left cursor-pointer group ${activeTab === 'memberships_audit' || activeTab === 'memberships_incomplete' || activeTab === 'memberships_manual'
                  ? 'bg-slate-800 text-white shadow-lg shadow-slate-800/10'
                  : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-202'
                  }`}
              >
                <div className="flex items-center gap-3">
                  <CreditCard className="w-4 h-4 text-slate-400 group-hover:text-slate-202" />
                  Memberships & Sales
                </div>
                <svg
                  className={`w-4 h-4 transition-transform duration-200 text-slate-400 ${membershipsMenuOpen ? 'rotate-180 text-white' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {membershipsMenuOpen && (
                <div className="flex flex-col pl-7 mt-1.5 gap-1.5 w-full">
                  <button
                    onClick={() => { setActiveTab('memberships_audit'); setMobileMenuOpen(false); }}
                    className={`flex items-center gap-3 px-4 py-2 rounded-lg text-xs font-semibold transition-all w-full text-left cursor-pointer ${activeTab === 'memberships_audit'
                      ? 'bg-[#2ab391] text-white shadow-sm'
                      : 'text-slate-400 hover:bg-slate-800/30 hover:text-slate-202'
                      }`}
                  >
                    Memberships
                  </button>
                  <button
                    onClick={() => { setActiveTab('memberships_incomplete'); setMobileMenuOpen(false); }}
                    className={`flex items-center gap-3 px-4 py-2 rounded-lg text-xs font-semibold transition-all w-full text-left cursor-pointer ${activeTab === 'memberships_incomplete'
                      ? 'bg-[#2ab391] text-white shadow-sm'
                      : 'text-slate-400 hover:bg-slate-800/30 hover:text-slate-202'
                      }`}
                  >
                    Incomplete Payments
                  </button>
                  {hasRole && hasRole('ADMIN') && (
                    <button
                      onClick={() => { setActiveTab('memberships_manual'); setMobileMenuOpen(false); }}
                      className={`flex items-center gap-3 px-4 py-2 rounded-lg text-xs font-semibold transition-all w-full text-left cursor-pointer ${activeTab === 'memberships_manual'
                        ? 'bg-[#2ab391] text-white shadow-sm'
                        : 'text-slate-400 hover:bg-slate-800/30 hover:text-slate-202'
                        }`}
                    >
                      Manual Memberships
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* 4. User Management */}
          {hasPermission('MANAGE_USERS') && (
            <button
              onClick={() => { setActiveTab('users'); setMobileMenuOpen(false); }}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all w-full text-left cursor-pointer group ${activeTab === 'users'
                ? 'bg-[#2ab391] text-white shadow-lg shadow-[#2ab391]/10'
                : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-202'
                }`}
            >
              <Users className="w-4 h-4 text-slate-400 group-hover:text-slate-202" />
              User Management
            </button>
          )}

          {/* 5. Magazine */}
          {(hasPermission('MANAGE_MEMBERSHIPS') || roles.includes('ROLE_MAGAZINE_SALES')) && (
            <div className="flex flex-col w-full">
              <button
                onClick={() => setMagazineMenuOpen(!magazineMenuOpen)}
                className={`flex items-center justify-between px-4 py-3 rounded-xl text-sm font-semibold transition-all w-full text-left cursor-pointer group ${['magazine_overview', 'magazine_subscribers', 'magazine_orders', 'magazine_manual', 'magazine_management'].includes(activeTab)
                  ? 'bg-slate-800 text-white shadow-lg shadow-slate-800/10'
                  : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-202'
                  }`}
              >
                <div className="flex items-center gap-3">
                  <BookOpen className="w-4 h-4 text-slate-400 group-hover:text-slate-202" />
                  Magazine
                </div>
                <svg
                  className={`w-4 h-4 transition-transform duration-200 text-slate-400 ${magazineMenuOpen ? 'rotate-180 text-white' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {magazineMenuOpen && (
                <div className="flex flex-col pl-7 mt-1.5 gap-1.5 w-full">
                  <button
                    onClick={() => { setActiveTab('magazine_overview'); setMobileMenuOpen(false); }}
                    className={`flex items-center gap-3 px-4 py-2 rounded-lg text-xs font-semibold transition-all w-full text-left cursor-pointer ${activeTab === 'magazine_overview'
                      ? 'bg-[#2ab391] text-white shadow-sm'
                      : 'text-slate-400 hover:bg-slate-800/30 hover:text-slate-202'
                      }`}
                  >
                    Overview
                  </button>
                  <button
                    onClick={() => { setActiveTab('magazine_subscribers'); setMobileMenuOpen(false); }}
                    className={`flex items-center gap-3 px-4 py-2 rounded-lg text-xs font-semibold transition-all w-full text-left cursor-pointer ${activeTab === 'magazine_subscribers'
                      ? 'bg-[#2ab391] text-white shadow-sm'
                      : 'text-slate-400 hover:bg-slate-800/30 hover:text-slate-202'
                      }`}
                  >
                    Subscriber
                  </button>
                  <button
                    onClick={() => { setActiveTab('magazine_orders'); setMobileMenuOpen(false); }}
                    className={`flex items-center gap-3 px-4 py-2 rounded-lg text-xs font-semibold transition-all w-full text-left cursor-pointer ${activeTab === 'magazine_orders'
                      ? 'bg-[#2ab391] text-white shadow-sm'
                      : 'text-slate-400 hover:bg-slate-800/30 hover:text-slate-202'
                      }`}
                  >
                    Order
                  </button>
                  {(roles.includes('ROLE_SUPER_ADMIN') || roles.includes('ROLE_ADMIN')) && (
                    <button
                      onClick={() => { setActiveTab('magazine_manual'); setMobileMenuOpen(false); }}
                      className={`flex items-center gap-3 px-4 py-2 rounded-lg text-xs font-semibold transition-all w-full text-left cursor-pointer ${activeTab === 'magazine_manual'
                        ? 'bg-[#2ab391] text-white shadow-sm'
                        : 'text-slate-400 hover:bg-slate-800/30 hover:text-slate-202'
                        }`}
                    >
                      Add Subscriber (Manual)
                    </button>
                  )}
                  {(roles.includes('ROLE_SUPER_ADMIN') || roles.includes('ROLE_ADMIN')) && (
                    <button
                      onClick={() => { setActiveTab('magazine_management'); setMobileMenuOpen(false); }}
                      className={`flex items-center gap-3 px-4 py-2 rounded-lg text-xs font-semibold transition-all w-full text-left cursor-pointer ${activeTab === 'magazine_management'
                        ? 'bg-[#2ab391] text-white shadow-sm'
                        : 'text-slate-400 hover:bg-slate-800/30 hover:text-slate-202'
                        }`}
                    >
                      Magazine Management
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* 6. Support Tickets */}
          {hasPermission('RESOLVE_ISSUES') && (
            <button
              onClick={() => { setActiveTab('support'); setMobileMenuOpen(false); }}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all w-full text-left cursor-pointer group ${activeTab === 'support'
                ? 'bg-[#2ab391] text-white shadow-lg shadow-[#2ab391]/10'
                : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-202'
                }`}
            >
              <Ticket className="w-4 h-4 text-slate-400 group-hover:text-slate-202" />
              Support Tickets
            </button>
          )}

          {/* 7. Categories & Tags */}
          {hasPermission('MANAGE_CATALOG') && (
            <button
              onClick={() => { setActiveTab('categories'); setMobileMenuOpen(false); }}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all w-full text-left cursor-pointer group ${activeTab === 'categories'
                ? 'bg-[#2ab391] text-white shadow-lg shadow-[#2ab391]/10'
                : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-202'
                }`}
            >
              <Tags className="w-4 h-4 text-slate-400 group-hover:text-slate-202" />
              Categories & Tags
            </button>
          )}

          {/* 8. Reported Reviews */}
          {hasPermission('RESOLVE_ISSUES') && (
            <button
              onClick={() => { setActiveTab('reported_reviews'); setMobileMenuOpen(false); }}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all w-full text-left cursor-pointer group ${activeTab === 'reported_reviews'
                ? 'bg-[#2ab391] text-white shadow-lg shadow-[#2ab391]/10'
                : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-202'
                }`}
            >
              <ShieldAlert className="w-4 h-4 text-slate-400 group-hover:text-slate-202" />
              Reported Reviews
            </button>
          )}

          {/* 9. Customers’ Inquiries */}
          {hasPermission('MANAGE_MEMBERSHIPS') && (
            <button
              onClick={() => { setActiveTab('inquiries'); setMobileMenuOpen(false); }}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all w-full text-left cursor-pointer group ${activeTab === 'inquiries'
                ? 'bg-[#2ab391] text-white shadow-lg shadow-[#2ab391]/10'
                : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-202'
                }`}
            >
              <MessageSquare className="w-4 h-4 text-slate-400 group-hover:text-slate-202" />
              Customers' Inquiries
            </button>
          )}

          {/* 10. Bulk Import (Dropdown group containing sub-items) */}
          {hasPermission('APPROVE_LISTINGS') && (
            <div className="flex flex-col w-full">
              <button
                onClick={() => setBulkImportMenuOpen(!bulkImportMenuOpen)}
                className={`flex items-center justify-between px-4 py-3 rounded-xl text-sm font-semibold transition-all w-full text-left cursor-pointer group ${activeTab === 'unclaimed_update' || activeTab === 'claim_requests' || activeTab === 'excel_import'
                  ? 'bg-slate-800 text-white shadow-lg shadow-slate-800/10'
                  : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-202'
                  }`}
              >
                <div className="flex items-center gap-3">
                  <FileSpreadsheet className="w-4 h-4 text-slate-400 group-hover:text-slate-202" />
                  Bulk Import
                </div>
                <svg
                  className={`w-4 h-4 transition-transform duration-200 text-slate-400 ${bulkImportMenuOpen ? 'rotate-180 text-white' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {bulkImportMenuOpen && (
                <div className="flex flex-col pl-7 mt-1.5 gap-1.5 w-full">
                  <button
                    onClick={() => { setActiveTab('unclaimed_update'); setMobileMenuOpen(false); }}
                    className={`flex items-center gap-3 px-4 py-2 rounded-lg text-xs font-semibold transition-all w-full text-left cursor-pointer ${activeTab === 'unclaimed_update'
                      ? 'bg-[#2ab391] text-white shadow-sm'
                      : 'text-slate-400 hover:bg-slate-800/30 hover:text-slate-202'
                      }`}
                  >
                    Unclaimed Listings
                  </button>
                  <button
                    onClick={() => { setActiveTab('claim_requests'); setMobileMenuOpen(false); }}
                    className={`flex items-center gap-3 px-4 py-2 rounded-lg text-xs font-semibold transition-all w-full text-left cursor-pointer ${activeTab === 'claim_requests'
                      ? 'bg-[#2ab391] text-white shadow-sm'
                      : 'text-slate-400 hover:bg-slate-800/30 hover:text-slate-202'
                      }`}
                  >
                    Listing Claim
                  </button>
                  <button
                    onClick={() => { setActiveTab('excel_import'); setMobileMenuOpen(false); }}
                    className={`flex items-center gap-3 px-4 py-2 rounded-lg text-xs font-semibold transition-all w-full text-left cursor-pointer ${activeTab === 'excel_import'
                      ? 'bg-[#2ab391] text-white shadow-sm'
                      : 'text-slate-400 hover:bg-slate-800/30 hover:text-slate-202'
                      }`}
                  >
                    Import
                  </button>
                </div>
              )}
            </div>
          )}
        </nav>

        {/* Mobile Sidebar Footer */}
        <div className="mt-auto pt-6 border-t border-slate-800 flex flex-col gap-2">
          <div className="flex items-center gap-3 bg-slate-800/40 p-3 rounded-xl border border-slate-800/60 select-none">
            <div className="w-9 h-9 rounded-lg bg-[#2ab391]/10 border border-[#2ab391]/20 flex items-center justify-center font-bold text-[#2ab391] text-sm">
              {user?.name ? user.name.charAt(0).toUpperCase() : 'A'}
            </div>
            <div className="overflow-hidden flex-1 text-left">
              <p className="text-xs font-bold text-slate-200 truncate">{user?.name || 'Admin User'}</p>
              <p className="text-[10px] text-slate-500 truncate mt-0.5">{user?.emailId || user?.email || 'admin@topinteriors.com'}</p>
            </div>
          </div>
          <button
            onClick={() => { logout(); setMobileMenuOpen(false); }}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold text-rose-400 hover:text-white bg-slate-800/40 hover:bg-rose-600/20 border border-slate-800 hover:border-rose-500/30 transition-all cursor-pointer active:scale-[0.98] select-none"
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* DESKTOP FIXED SIDEBAR NAVIGATION */}
      <aside className={`hidden lg:flex flex-col fixed inset-y-0 left-0 bg-slate-900 text-slate-100 py-6 z-30 justify-between border-r border-slate-800 transition-all duration-300 ${sidebarOpen ? 'w-64 xl:w-72 px-6' : 'w-20 px-3'
        }`}>
        {/* Logo Header */}
        <div className={`flex items-center pb-6 border-b border-slate-800 mb-6 flex-shrink-0 select-none ${sidebarOpen ? 'justify-between' : 'justify-center'}`}>
          {sidebarOpen ? (
            <img src="/Top_Interior_India_2.png" alt="Top Interiors Logo" className="h-9 object-contain" />
          ) : (
            <div className="w-9 h-9 rounded-xl bg-[#2ab391] flex items-center justify-center font-black text-white text-base">T</div>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-slate-500 hover:text-white p-1 rounded-lg hover:bg-slate-800/50 transition-colors cursor-pointer hidden sm:block"
            title={sidebarOpen ? "Collapse Navigation" : "Expand Navigation"}
          >
            {sidebarOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        </div>

        {/* Sidebar Nav Items */}
        <nav className="flex-1 space-y-1.5 overflow-y-auto no-scrollbar scrollbar-none pr-1 select-none">
          {/* 1. Overview */}
          {(!roles.includes('ROLE_MAGAZINE_SALES') || roles.includes('ROLE_SUPER_ADMIN') || roles.includes('ROLE_ADMIN')) && (
            <button
              onClick={() => setActiveTab('overview')}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all w-full text-left cursor-pointer group ${activeTab === 'overview'
                ? 'bg-[#2ab391] text-white shadow-lg shadow-[#2ab391]/10'
                : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-202'
                }`}
            >
              <LayoutDashboard className="w-4 h-4 text-slate-400 group-hover:text-slate-202" />
              {sidebarOpen && <span>Overview</span>}
            </button>
          )}

          {/* 2. Listing */}
          {hasPermission('APPROVE_LISTINGS') && (
            <>
              <button
                onClick={() => setActiveTab('listings')}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all w-full text-left cursor-pointer group ${activeTab === 'listings'
                  ? 'bg-[#2ab391] text-white shadow-lg shadow-[#2ab391]/10'
                  : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-202'
                  }`}
              >
                <ClipboardCheck className="w-4 h-4 text-slate-400 group-hover:text-slate-202" />
                {sidebarOpen && <span>Listing</span>}
              </button>
              <button
                onClick={() => setActiveTab('vendor_listing_manager')}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all w-full text-left cursor-pointer group mt-1.5 ${activeTab === 'vendor_listing_manager'
                  ? 'bg-[#2ab391] text-white shadow-lg shadow-[#2ab391]/10'
                  : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-202'
                  }`}
              >
                <UserCheck className="w-4 h-4 text-slate-400 group-hover:text-slate-202" />
                {sidebarOpen && <span>Vendor Listing Manager</span>}
              </button>
            </>
          )}

          {/* 3. Memberships & Sales */}
          {hasPermission('MANAGE_MEMBERSHIPS') && (
            <div className="flex flex-col w-full">
              <button
                onClick={() => setMembershipsMenuOpen(!membershipsMenuOpen)}
                className={`flex items-center justify-between px-4 py-3 rounded-xl text-sm font-semibold transition-all w-full text-left cursor-pointer group ${activeTab === 'memberships_audit' || activeTab === 'memberships_incomplete' || activeTab === 'memberships_manual'
                  ? 'bg-slate-800 text-white'
                  : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-202'
                  }`}
              >
                <div className="flex items-center gap-3">
                  <CreditCard className="w-4 h-4 text-slate-400 group-hover:text-slate-202" />
                  {sidebarOpen && <span>Memberships & Sales</span>}
                </div>
                {sidebarOpen && (
                  <svg
                    className={`w-4 h-4 transition-transform duration-200 text-slate-400 ${membershipsMenuOpen ? 'rotate-180 text-white' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                )}
              </button>

              {membershipsMenuOpen && sidebarOpen && (
                <div className="flex flex-col pl-7 mt-1.5 gap-1.5 w-full">
                  <button
                    onClick={() => setActiveTab('memberships_audit')}
                    className={`flex items-center gap-3 px-4 py-2 rounded-lg text-xs font-semibold transition-all w-full text-left cursor-pointer ${activeTab === 'memberships_audit'
                      ? 'bg-[#2ab391] text-white shadow-sm'
                      : 'text-slate-400 hover:bg-slate-800/30 hover:text-slate-202'
                      }`}
                  >
                    Memberships
                  </button>
                  <button
                    onClick={() => setActiveTab('memberships_incomplete')}
                    className={`flex items-center gap-3 px-4 py-2 rounded-lg text-xs font-semibold transition-all w-full text-left cursor-pointer ${activeTab === 'memberships_incomplete'
                      ? 'bg-[#2ab391] text-white shadow-sm'
                      : 'text-slate-400 hover:bg-slate-800/30 hover:text-slate-202'
                      }`}
                  >
                    Incomplete Payments
                  </button>
                  {hasRole && hasRole('ADMIN') && (
                    <button
                      onClick={() => setActiveTab('memberships_manual')}
                      className={`flex items-center gap-3 px-4 py-2 rounded-lg text-xs font-semibold transition-all w-full text-left cursor-pointer ${activeTab === 'memberships_manual'
                        ? 'bg-[#2ab391] text-white shadow-sm'
                        : 'text-slate-400 hover:bg-slate-800/30 hover:text-slate-202'
                        }`}
                    >
                      Manual Memberships
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* 4. User Management */}
          {hasPermission('MANAGE_USERS') && (
            <button
              onClick={() => setActiveTab('users')}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all w-full text-left cursor-pointer group ${activeTab === 'users'
                ? 'bg-[#2ab391] text-white shadow-lg shadow-[#2ab391]/10'
                : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-202'
                }`}
            >
              <Users className="w-4 h-4 text-slate-400 group-hover:text-slate-202" />
              {sidebarOpen && <span>User Management</span>}
            </button>
          )}

          {/* 5. Magazine */}
          {(hasPermission('MANAGE_MEMBERSHIPS') || roles.includes('ROLE_MAGAZINE_SALES')) && (
            <div className="flex flex-col w-full">
              <button
                onClick={() => setMagazineMenuOpen(!magazineMenuOpen)}
                className={`flex items-center justify-between px-4 py-3 rounded-xl text-sm font-semibold transition-all w-full text-left cursor-pointer group ${['magazine_overview', 'magazine_subscribers', 'magazine_orders', 'magazine_manual', 'magazine_management'].includes(activeTab)
                  ? 'bg-slate-800 text-white'
                  : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-202'
                  }`}
              >
                <div className="flex items-center gap-3">
                  <BookOpen className="w-4 h-4 text-slate-400 group-hover:text-slate-205" />
                  {sidebarOpen && <span>Magazine</span>}
                </div>
                {sidebarOpen && (
                  <svg
                    className={`w-4 h-4 transition-transform duration-200 text-slate-400 ${magazineMenuOpen ? 'rotate-180 text-white' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                )}
              </button>

              {magazineMenuOpen && sidebarOpen && (
                <div className="flex flex-col pl-7 mt-1.5 gap-1.5 w-full">
                  <button
                    onClick={() => setActiveTab('magazine_overview')}
                    className={`flex items-center gap-3 px-4 py-2 rounded-lg text-xs font-semibold transition-all w-full text-left cursor-pointer ${activeTab === 'magazine_overview'
                      ? 'bg-[#2ab391] text-white shadow-sm'
                      : 'text-slate-400 hover:bg-slate-800/30 hover:text-slate-202'
                      }`}
                  >
                    Overview
                  </button>
                  <button
                    onClick={() => setActiveTab('magazine_subscribers')}
                    className={`flex items-center gap-3 px-4 py-2 rounded-lg text-xs font-semibold transition-all w-full text-left cursor-pointer ${activeTab === 'magazine_subscribers'
                      ? 'bg-[#2ab391] text-white shadow-sm'
                      : 'text-slate-400 hover:bg-slate-800/30 hover:text-slate-202'
                      }`}
                  >
                    Subscriber
                  </button>
                  <button
                    onClick={() => setActiveTab('magazine_orders')}
                    className={`flex items-center gap-3 px-4 py-2 rounded-lg text-xs font-semibold transition-all w-full text-left cursor-pointer ${activeTab === 'magazine_orders'
                      ? 'bg-[#2ab391] text-white shadow-sm'
                      : 'text-slate-400 hover:bg-slate-800/30 hover:text-slate-202'
                      }`}
                  >
                    Order
                  </button>
                  {(roles.includes('ROLE_SUPER_ADMIN') || roles.includes('ROLE_ADMIN')) && (
                    <button
                      onClick={() => setActiveTab('magazine_manual')}
                      className={`flex items-center gap-3 px-4 py-2 rounded-lg text-xs font-semibold transition-all w-full text-left cursor-pointer ${activeTab === 'magazine_manual'
                        ? 'bg-[#2ab391] text-white shadow-sm'
                        : 'text-slate-400 hover:bg-slate-800/30 hover:text-slate-202'
                        }`}
                    >
                      Add Subscriber (Manual)
                    </button>
                  )}
                  {(roles.includes('ROLE_SUPER_ADMIN') || roles.includes('ROLE_ADMIN')) && (
                    <button
                      onClick={() => setActiveTab('magazine_management')}
                      className={`flex items-center gap-3 px-4 py-2 rounded-lg text-xs font-semibold transition-all w-full text-left cursor-pointer ${activeTab === 'magazine_management'
                        ? 'bg-[#2ab391] text-white shadow-sm'
                        : 'text-slate-400 hover:bg-slate-800/30 hover:text-slate-202'
                        }`}
                    >
                      Magazine Management
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* 6. Support Tickets */}
          {hasPermission('RESOLVE_ISSUES') && (
            <button
              onClick={() => setActiveTab('support')}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all w-full text-left cursor-pointer group ${activeTab === 'support'
                ? 'bg-[#2ab391] text-white shadow-lg shadow-[#2ab391]/10'
                : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-202'
                }`}
            >
              <Ticket className="w-4 h-4 text-slate-400 group-hover:text-slate-202" />
              {sidebarOpen && <span>Support Tickets</span>}
            </button>
          )}

          {/* 7. Categories & Tags */}
          {hasPermission('MANAGE_CATALOG') && (
            <button
              onClick={() => setActiveTab('categories')}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all w-full text-left cursor-pointer group ${activeTab === 'categories'
                ? 'bg-[#2ab391] text-white shadow-lg shadow-[#2ab391]/10'
                : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-202'
                }`}
            >
              <Tags className="w-4 h-4 text-slate-400 group-hover:text-slate-202" />
              {sidebarOpen && <span>Categories & Tags</span>}
            </button>
          )}

          {/* 8. Reported Reviews */}
          {hasPermission('RESOLVE_ISSUES') && (
            <button
              onClick={() => setActiveTab('reported_reviews')}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all w-full text-left cursor-pointer group ${activeTab === 'reported_reviews'
                ? 'bg-[#2ab391] text-white shadow-lg shadow-[#2ab391]/10'
                : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-202'
                }`}
            >
              <ShieldAlert className="w-4 h-4 text-slate-400 group-hover:text-slate-202" />
              {sidebarOpen && <span>Reported Reviews</span>}
            </button>
          )}

          {/* 9. Customers' Inquiries */}
          {hasPermission('MANAGE_MEMBERSHIPS') && (
            <button
              onClick={() => setActiveTab('inquiries')}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all w-full text-left cursor-pointer group mt-1.5 ${activeTab === 'inquiries'
                ? 'bg-[#2ab391] text-white shadow-lg shadow-[#2ab391]/10'
                : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-202'
                }`}
            >
              <MessageSquare className="w-4 h-4 text-slate-400 group-hover:text-slate-202" />
              {sidebarOpen && <span>Customers' Inquiries</span>}
            </button>
          )}

          {/* 10. Bulk Import */}
          {hasPermission('APPROVE_LISTINGS') && (
            <div className="flex flex-col w-full">
              <button
                onClick={() => setBulkImportMenuOpen(!bulkImportMenuOpen)}
                className={`flex items-center justify-between px-4 py-3 rounded-xl text-sm font-semibold transition-all w-full text-left cursor-pointer group ${activeTab === 'unclaimed_update' || activeTab === 'claim_requests' || activeTab === 'excel_import'
                  ? 'bg-slate-800 text-white shadow-lg shadow-slate-800/10'
                  : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-202'
                  }`}
              >
                <div className="flex items-center gap-3">
                  <FileSpreadsheet className="w-4 h-4 text-slate-400 group-hover:text-slate-202" />
                  {sidebarOpen && <span>Bulk Import</span>}
                </div>
                {sidebarOpen && (
                  <svg
                    className={`w-4 h-4 transition-transform duration-200 text-slate-400 ${bulkImportMenuOpen ? 'rotate-180 text-white' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                )}
              </button>

              {bulkImportMenuOpen && sidebarOpen && (
                <div className="flex flex-col pl-7 mt-1.5 gap-1.5 w-full">
                  <button
                    onClick={() => setActiveTab('unclaimed_update')}
                    className={`flex items-center gap-3 px-4 py-2 rounded-lg text-xs font-semibold transition-all w-full text-left cursor-pointer ${activeTab === 'unclaimed_update'
                      ? 'bg-[#2ab391] text-white shadow-sm'
                      : 'text-slate-400 hover:bg-slate-800/30 hover:text-slate-202'
                      }`}
                  >
                    Unclaimed Listings
                  </button>
                  <button
                    onClick={() => setActiveTab('claim_requests')}
                    className={`flex items-center gap-3 px-4 py-2 rounded-lg text-xs font-semibold transition-all w-full text-left cursor-pointer ${activeTab === 'claim_requests'
                      ? 'bg-[#2ab391] text-white shadow-sm'
                      : 'text-slate-400 hover:bg-slate-800/30 hover:text-slate-202'
                      }`}
                  >
                    Listing Claim
                  </button>
                  <button
                    onClick={() => setActiveTab('excel_import')}
                    className={`flex items-center gap-3 px-4 py-2 rounded-lg text-xs font-semibold transition-all w-full text-left cursor-pointer ${activeTab === 'excel_import'
                      ? 'bg-[#2ab391] text-white shadow-sm'
                      : 'text-slate-400 hover:bg-slate-800/30 hover:text-slate-202'
                      }`}
                  >
                    Import
                  </button>
                </div>
              )}
            </div>
          )}
        </nav>

        {/* Desktop Sidebar Footer */}
        <div className="mt-auto pt-6 border-t border-slate-800 flex flex-col gap-2 flex-shrink-0">
          <div className={`flex items-center bg-slate-800/40 p-3 rounded-xl border border-slate-850 select-none ${sidebarOpen ? 'gap-3' : 'justify-center'}`}>
            <div className="w-9 h-9 rounded-lg bg-[#2ab391]/10 border border-[#2ab391]/20 flex items-center justify-center font-bold text-[#2ab391] text-sm">
              {user?.name ? user.name.charAt(0).toUpperCase() : 'A'}
            </div>
            {sidebarOpen && (
              <div className="overflow-hidden flex-1 text-left">
                <p className="text-xs font-bold text-slate-200 truncate">{user?.name || 'Admin User'}</p>
                <p className="text-[10px] text-slate-500 truncate mt-0.5">{user?.emailId || user?.email || 'admin@topinteriors.com'}</p>
              </div>
            )}
          </div>
          <button
            onClick={logout}
            className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold text-rose-400 hover:text-white bg-slate-800/40 hover:bg-rose-600/25 border border-slate-800 hover:border-rose-500/20 transition-all cursor-pointer active:scale-[0.98] select-none`}
          >
            <LogOut className="w-3.5 h-3.5" />
            {sidebarOpen && <span>Sign Out</span>}
          </button>
        </div>
      </aside>

      {/* MAIN CONTENT AREA */}
      <div className={`flex-1 flex flex-col min-h-screen min-w-0 transition-all duration-300 ${sidebarOpen ? 'lg:pl-64 xl:pl-72' : 'lg:pl-20'}`}>

        {/* MAIN TOP BAR / HEADER */}
        <header className="hidden lg:flex items-center justify-between px-8 py-5 bg-white border-b border-gray-200 sticky top-0 z-30 select-none shadow-sm">
          <div className="flex flex-col text-left">
            <h1 className="text-xl font-black text-gray-800 tracking-tight">{getAdminDashboardTitle()}</h1>
            <p className="text-xs text-gray-400 mt-0.5 font-medium font-sans">Access central control configurations and moderation audit actions.</p>
          </div>

          <div className="flex items-center gap-5">
            {/* Global Notifications Bell System */}
            <div className="relative" ref={notificationRef}>
              <button
                onClick={() => setNotificationMenuOpen(!notificationMenuOpen)}
                className="w-10 h-10 rounded-xl hover:bg-gray-100/80 border border-gray-200 flex items-center justify-center relative transition-all active:scale-95 cursor-pointer text-gray-600 hover:text-gray-900"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {totalNotificationCount > 0 && (
                  <span className="absolute top-1.5 right-1.5 w-5 h-5 bg-red-500 border-2 border-white rounded-full text-[9px] font-extrabold text-white flex items-center justify-center shadow-sm">
                    {totalNotificationCount}
                  </span>
                )}
              </button>

              {notificationMenuOpen && (
                <div className="absolute right-0 mt-3.5 w-96 bg-white border border-gray-200 rounded-2xl shadow-xl z-[45] animate-in fade-in slide-in-from-top-3 duration-200">
                  <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-slate-50/50 rounded-t-2xl">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-bold text-gray-800">Alert Center</span>
                      <span className="px-2 py-0.5 text-[10px] font-extrabold bg-[#2ab391] text-white rounded-full">
                        {totalNotificationCount} Active
                      </span>
                    </div>
                    <button
                      onClick={() => setNotificationMenuOpen(false)}
                      className="text-[10px] font-bold text-gray-400 hover:text-gray-700 hover:underline cursor-pointer"
                    >
                      Close
                    </button>
                  </div>

                  <div className="max-h-80 overflow-y-auto divide-y divide-gray-50">
                    {notifications.length === 0 ? (
                      <div className="p-8 text-center text-gray-400 font-medium text-xs">
                        <svg className="w-8 h-8 text-gray-300 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        All clear! No new notifications.
                      </div>
                    ) : (
                      notifications.map(n => (
                        <div
                          key={n.id}
                          onClick={() => {
                            handleNotificationClick(n);
                            setNotificationMenuOpen(false);
                          }}
                          className="p-4 hover:bg-slate-50/70 transition-colors cursor-pointer text-left group"
                        >
                          <div className="flex justify-between items-start gap-2.5">
                            <span className={`text-[10px] font-bold uppercase ${
                              n.type.includes('LISTING') ? 'text-[#2ab391]' :
                              n.type.includes('TICKET') ? 'text-amber-600' :
                              n.type.includes('MEMBERSHIP') ? 'text-blue-600' :
                              n.type.includes('MAGAZINE') ? 'text-purple-600' :
                              'text-violet-600'
                            }`}>
                              {n.title}
                            </span>
                            <span className="text-[9px] text-gray-400 font-semibold whitespace-nowrap">
                              {new Date(n.time).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-xs text-gray-600 leading-relaxed font-semibold mt-1 group-hover:text-gray-900 transition-colors">
                            {n.message}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Profile badge info */}
            <div className="flex items-center gap-3 bg-gray-50 border border-gray-200 p-2 rounded-xl">
              <div className="w-8 h-8 rounded-lg bg-[#2ab391] text-white flex items-center justify-center font-bold text-sm">
                {user?.name ? user.name.charAt(0).toUpperCase() : 'A'}
              </div>
              <div className="flex flex-col text-left">
                <span className="text-xs font-bold text-gray-800">{user?.name || 'Admin User'}</span>
                <span className="text-[9px] text-gray-400 font-bold uppercase tracking-wider">{roles ? roles[0].replace('ROLE_', '').replace('_', ' ') : 'Administrator'}</span>
              </div>
            </div>
          </div>
        </header>

        {/* CONTAINER FOR TABS RENDER */}
        <main className="flex-1 p-6 lg:p-8 mt-14 lg:mt-0 select-text min-w-0 overflow-hidden">
          <div className="transition-all duration-300">
            {/* 1. OVERVIEW TAB */}
            {activeTab === 'overview' && (!roles.includes('ROLE_MAGAZINE_SALES') || roles.includes('ROLE_SUPER_ADMIN') || roles.includes('ROLE_ADMIN')) && (
              <OverviewTab toast={toast} setActiveTab={setActiveTab} setListingSubTab={setListingSubTab} />
            )}

            {/* 2. LISTING APPROVALS TAB */}
            {activeTab === 'listings' && hasPermission('APPROVE_LISTINGS') && (
              <ListingApprovalsTab
                toast={toast}
                setLightboxImage={setLightboxImage}
                listingSubTab={listingSubTab}
                setListingSubTab={setListingSubTab}
              />
            )}

            {/* 3. IMPORT LISTINGS TAB */}
            {activeTab === 'excel_import' && hasPermission('APPROVE_LISTINGS') && (
              <ExcelImportTab toast={toast} />
            )}

            {/* 4. CLAIM REQUESTS TAB */}
            {activeTab === 'claim_requests' && hasPermission('APPROVE_LISTINGS') && (
              <ClaimRequestsTab toast={toast} />
            )}

            {/* 5. MEMBERSHIP AUDIT TAB */}
            {activeTab === 'memberships_audit' && hasPermission('MANAGE_MEMBERSHIPS') && (
              <MembershipAuditTab toast={toast} />
            )}

            {/* 6. INCOMPLETE MEMBERSHIPS TAB */}
            {activeTab === 'memberships_incomplete' && hasPermission('MANAGE_MEMBERSHIPS') && (
              <IncompleteMembershipsTab />
            )}

            {/* 7. MANUAL MEMBERSHIPS TAB */}
            {activeTab === 'memberships_manual' && hasPermission('MANAGE_MEMBERSHIPS') && hasRole && hasRole('ADMIN') && (
              <ManualMembershipsTab toast={toast} />
            )}

            {/* 8. SUPPORT TICKETS TAB */}
            {activeTab === 'support' && hasPermission('RESOLVE_ISSUES') && (
              <SupportTicketsTab toast={toast} setLightboxImage={setLightboxImage} />
            )}

            {/* 9. REPORTED REVIEWS TAB */}
            {activeTab === 'reported_reviews' && hasPermission('RESOLVE_ISSUES') && (
              <ReportedReviewsTab toast={toast} setLightboxImage={setLightboxImage} />
            )}

            {/* 10. USER AUDIT & ROLES TAB */}
            {activeTab === 'users' && hasPermission('MANAGE_USERS') && (
              <ManageUsersTab toast={toast} />
            )}

            {/* 11. VENDOR LISTING INQUIRIES TAB */}
            {activeTab === 'inquiries' && hasPermission('MANAGE_MEMBERSHIPS') && (
              <InquiriesTab />
            )}

            {/* 12. CATEGORIES & TAGS TAB */}
            {activeTab === 'categories' && hasPermission('MANAGE_CATALOG') && (
              <CategoriesTagsTab toast={toast} />
            )}

            {/* 13. UNCLAIMED UPDATE TAB */}
            {activeTab === 'unclaimed_update' && hasPermission('APPROVE_LISTINGS') && (
              <UnclaimedUpdateTab toast={toast} />
            )}

            {/* 13b. VENDOR LISTING MANAGER TAB */}
            {activeTab === 'vendor_listing_manager' && hasPermission('APPROVE_LISTINGS') && (
              <VendorListingManagerTab toast={toast} />
            )}

            {/* 14. MAGAZINE OVERVIEW */}
            {activeTab === 'magazine_overview' && (hasPermission('MANAGE_MEMBERSHIPS') || roles.includes('ROLE_MAGAZINE_SALES')) && (
              <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden p-6 text-left">
                <MagazineOverview toast={toast} />
              </div>
            )}

            {/* 15. MAGAZINE SUBSCRIBERS */}
            {activeTab === 'magazine_subscribers' && (hasPermission('MANAGE_MEMBERSHIPS') || roles.includes('ROLE_MAGAZINE_SALES')) && (
              <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden p-6 text-left">
                <MagazineSubscribers toast={toast} />
              </div>
            )}

            {/* 16. MAGAZINE ORDERS */}
            {activeTab === 'magazine_orders' && (hasPermission('MANAGE_MEMBERSHIPS') || roles.includes('ROLE_MAGAZINE_SALES')) && (
              <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden p-6 text-left">
                <MagazineOrders toast={toast} />
              </div>
            )}

            {/* 17. MAGAZINE MANUAL */}
            {activeTab === 'magazine_manual' && (roles.includes('ROLE_SUPER_ADMIN') || roles.includes('ROLE_ADMIN')) && (
              <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden p-6 text-left">
                <MagazineManual toast={toast} />
              </div>
            )}

            {/* 18. MAGAZINE MANAGEMENT */}
            {activeTab === 'magazine_management' && (roles.includes('ROLE_SUPER_ADMIN') || roles.includes('ROLE_ADMIN')) && (
              <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden p-6 text-left">
                <MagazineManagement toast={toast} />
              </div>
            )}
          </div>

          {/* Picture Box Lightbox Modal */}
          {lightboxImage && (
            <div
              className="fixed inset-0 bg-slate-900/90 backdrop-blur-md z-[60] flex items-center justify-center p-4 animate-in fade-in duration-200"
              onClick={() => setLightboxImage(null)}
            >
              <div className="relative max-w-5xl w-full max-h-[85vh] flex items-center justify-center flex-col">
                <button
                  onClick={() => setLightboxImage(null)}
                  className="absolute -top-12 right-0 bg-white/10 hover:bg-white/20 text-white p-2.5 rounded-full border border-white/25 hover:border-white/50 transition-all cursor-pointer"
                  title="Close Lightbox"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <img
                  src={lightboxImage}
                  alt="Zoomed Attachment"
                  className="max-w-full max-h-[80vh] object-contain rounded-xl shadow-2xl border border-white/10 animate-in zoom-in-95 duration-200"
                  onClick={(e) => e.stopPropagation()}
                />
                <p className="text-white/60 text-xs font-semibold mt-4 bg-slate-850 px-3 py-1.5 rounded-full border border-white/10 select-none">
                  Click anywhere outside the image or press the button to close
                </p>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
